import React, { useState, useEffect, useRef } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  FormLabel,
  Link,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { Path } from "components/app.component";
import {
  getDeliveryPoints,
  updateDeliveryInfo,
} from "services/rentals-thin.service";
import { useNavigate } from "react-router-dom";
import "./delivery-information.css";
import { DeliveryPoint } from "store/rentals-store";
import {
  GlobalState,
  Rental,
  setNavigation,
  store,
  updateDelivery,
  disableButton,
  enableButton,
} from "store/rentals-store";
import { useSelector } from "react-redux";
import Iframe from "components/atoms/iframe/iframe";

export default function DeliveryInformation() {
  const navigation = useNavigate();
  const rental: Rental = useSelector((state: GlobalState) => state.rental);
  const path: string = useSelector(
    (state: GlobalState) => state.navigation.currentPath
  );
  const [deliveryPointId, setDeliveryPointId] = useState(
    rental.delivery?.deliveryPointId || ""
  );
  const [points, setPoints] = useState<DeliveryPoint[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [retryCount, setRetryCount] = useState(0);
  const [loadedFor, setLoadedFor] = useState("");
  const submitting = useRef(false);
  const carIdsKey = JSON.stringify(rental.totals.items.map((item) => item.id));
  const requestKey = `${rental.rentalId}:${carIdsKey}`;
  const isReady = loadedFor === requestKey && !isLoading && !loadError;
  const selectedPoint = isReady
    ? points.find((point) => point.id === deliveryPointId)
    : undefined;

  useEffect(() => {
    setDeliveryPointId(rental.delivery?.deliveryPointId || "");
  }, [rental.delivery?.deliveryPointId]);

  useEffect(() => {
    let cancelled = false;
    const carIds: string[] = JSON.parse(carIdsKey);
    setIsLoading(true);
    setLoadError("");
    setSubmitError("");
    setPoints([]);

    if (!rental.rentalId || carIds.length === 0) {
      setLoadError("Selecciona un vehículo para consultar los lugares de entrega.");
      setIsLoading(false);
      return;
    }

    getDeliveryPoints(carIds)
      .then((response) => {
        if (cancelled) return;
        setPoints(response.data.filter((point) => point.active));
        setLoadedFor(requestKey);
        setIsLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadError("No se pudieron cargar los lugares de entrega.");
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [carIdsKey, rental.rentalId, requestKey, retryCount]);

  useEffect(() => {
    if (path !== Path.Payment || submitting.current) return;

    if (!selectedPoint) {
      store.dispatch(setNavigation(Path.Delivery));
      return;
    }

    const point = selectedPoint;
    submitting.current = true;
    async function submit() {
      try {
        const response = await updateDeliveryInfo({ deliveryPointId: point.id });
        store.dispatch(updateDelivery({ ...response.data, deliveryPoint: point }));
        navigation(Path.Payment);
        return;
      } catch (error) {
        console.error("there was an error updating delivery info");
        setSubmitError("No se pudo guardar el lugar de entrega. Intenta de nuevo.");
        store.dispatch(setNavigation(Path.Delivery));
        submitting.current = false;
        return;
      }
    }

    void submit();
  }, [selectedPoint, path, navigation]);

  useEffect(() => {
    store.dispatch(selectedPoint ? enableButton() : disableButton());
    return () => {
      store.dispatch(disableButton());
    };
  }, [selectedPoint]);

  const selectedAddress = selectedPoint
    ? [
        selectedPoint.address.streetName,
        selectedPoint.address.apartmentNumber,
        selectedPoint.address.neighborhood,
        selectedPoint.address.addressLine2,
        selectedPoint.address.town,
        selectedPoint.address.region,
        selectedPoint.address.zipCode,
        selectedPoint.address.country,
      ].filter(Boolean).join(", ")
    : "";
  const locationUrl = selectedPoint?.address.locationUrl;

  return (
    <Container>
      <Typography
        fontSize={{
          lg: 35,
          md: 25,
          sm: 20,
          xs: 20,
        }}
      >
        {" "}
        Lugar de entrega
      </Typography>
      {(isLoading || (!loadError && loadedFor !== requestKey)) && (
        <Box role="status" sx={{ display: "flex", alignItems: "center", gap: 1, my: 2 }}>
          <CircularProgress size={20} />
          <Typography>Cargando lugares de entrega...</Typography>
        </Box>
      )}
      {loadError && (
        <Alert severity="error" sx={{ my: 2 }} action={
          <Button color="inherit" onClick={() => setRetryCount((count) => count + 1)}>
            Reintentar
          </Button>
        }>
          {loadError}
        </Alert>
      )}
      {isReady && points.length === 0 && (
        <Alert severity="info" sx={{ my: 2 }}>
          No hay lugares de entrega disponibles para los vehículos seleccionados.
        </Alert>
      )}
      {submitError && <Alert severity="error" sx={{ my: 2 }}>{submitError}</Alert>}
      {isReady && points.length > 0 && <FormControl fullWidth>
        <FormLabel id="delivery-information">
          Selecciona el lugar de preferencia para la entrega del vehículo
        </FormLabel>
        <RadioGroup
          aria-labelledby="delivery-information"
          value={selectedPoint?.id || ""}
          name="delivery"
          onChange={(e) => {
            setDeliveryPointId(e.currentTarget.value);
            setSubmitError("");
          }}
        >
          {points.map((point) => (
            <FormControlLabel
              key={point.id}
              value={point.id}
              control={<Radio sx={{ "&& input": { pointerEvents: "auto" } }} />}
              label={point.name}
              sx={{ alignItems: "flex-start", "& .MuiFormControlLabel-label": { py: 1, overflowWrap: "anywhere" } }}
            />
          ))}
        </RadioGroup>
      </FormControl>}
      {selectedPoint && (
        <Box sx={{ my: 2, overflowWrap: "anywhere" }}>
          <Typography>{selectedAddress}</Typography>
          {selectedPoint.address.phoneNumber && <Typography>{selectedPoint.address.phoneNumber}</Typography>}
          {locationUrl && /^https?:\/\//i.test(locationUrl) && (
            <Link href={locationUrl} target="_blank" rel="noopener noreferrer">Ver ubicación</Link>
          )}
          <Box sx={{ height: { xs: 260, md: 360 }, mt: 1 }}>
            <Iframe src={`https://maps.google.com/maps?q=${encodeURIComponent(selectedAddress)}&output=embed`} />
          </Box>
        </Box>
      )}
      <Box>
        Nota: el lugar seleccionado para la entrega será el mismo para la
        devolución
      </Box>
    </Container>
  );
}
